// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import useConfigList from '@Hooks/useConfigList';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import CaseTemplateManage from '@Organisms/CaseTemplate/CaseTemplateManage/CaseTemplateManage';
import TemplateImport from '@Organisms/TemplateImport/TemplateImport';
import { ObjectTypeV1 as ObjectType, CaseTemplateOutput, PermissionsEnum } from 'thehive-sdk';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import { useParams } from 'react-router';
import CaseTemplateListDetails from '@Organisms/CaseTemplate/CaseTemplateListDetails/CaseTemplateListDetails';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import DisplayEntityInfo from '@Organisms/DisplayEntityInfo/DisplayEntityInfo';
import { CaseTemplateListFlags } from '@Organisms/CaseTemplate/CaseTemplateListFlags/CaseTemplateListFlags';
import { computeEventList } from '@Organisms/CaseTemplate/eventList';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatMultipleDate } from '@Molecules/Format';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { ICONS } from '@Constants/iconsApp';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { downloadFile } from '@Helpers/download';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { StatusContext } from '@Context/StatusProvider';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { useCurrentUser } from '@Queries/userQueries';
import usePageTitle from '@Hooks/usePageTitle';

import './CaseTemplateList.scss';
// import SecurityTag from '@Atoms/Info/SecurityTag/SecurityTag';

export const CaseTemplatesList = (): ReactElement => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();

    const { quotas } = useContext(StatusContext);

    const config = useConfigList<CaseTemplateOutput>({
        selector: {
            head: [{ name: 'getOrganisation', idOrName: orgId }, { name: 'caseTemplates' }],
            name: 'get-case-templates',
            extraData: ['pageTemplateHeaders'],
        },
        controller: client.CaseTemplateController,
        storageId: StorageFilterEnum.CASE_TEMPLATE,
        objectType: ObjectType.CASE_TEMPLATE,
    });

    const {
        dataSource = [],
        columnsRenderer,
        listRefetch,
        status = 'loading',
        countRefetch,
        filterManager,
        paginationManager,
        table,
        storageViewManager,
        storagePageManager,
    } = config;

    const { setDisplay, setOnLoadView } = storagePageManager || {};
    const { setDefaultDisplay } = storageViewManager || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CaseTemplateController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    listRefetch();
                    if (message) {
                        openMessage('success', message);
                    }

                    if (count) {
                        countRefetch();
                    }
                },
            }),
        [listRefetch, countRefetch, i],
    );
    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        createdAt: { label: i('common.created-date'), visible: true },
        updatedAt: { label: i('common.updated-date'), visible: true },
    });

    setOnLoadView?.((viewContent) => {
        loadVisibleStateDates({
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};
    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_CASE_TEMPLATE]);

    const columns = columnsRenderer([
        {
            className: 'case-template-list__column__flags',
            render: (caseTemplate, { custom: filterCustom }): ReactElement => (
                <CaseTemplateListFlags
                    tlp={caseTemplate.tlp}
                    severity={caseTemplate.severity}
                    onClick={(field): void => filterCustom({ field, operator: 'any', values: [caseTemplate[field]] })}
                />
            ),
        },
        {
            title: [{ label: i('case-template.list.table.columns.display-name'), sortField: 'displayName' }],
            className: 'case-template-list__column__display-name column_preview',
            render: (caseTemplate, { tags: filterTags }): ReactElement => (
                <CaseTemplateListDetails
                    caseTemplate={caseTemplate}
                    onTagClick={(tag, { altKey }): void => filterTags([tag], altKey)}
                />
            ),
        },
        {
            title: i('case-template.list.table.columns.name'),
            className: 'case-template-list__column__name',
            render: ({ name }): ReactElement => <div>{name}</div>,
        },
        {
            title: i('case-template.list.table.columns.details'),
            className: 'case-template-list__column__details',
            render: ({ tasks = [], customFields = [], extraData }): ReactElement => (
                <>
                    <DisplayEntityInfo count={tasks.length} label={i('common.tasks')} />
                    <DisplayEntityInfo count={customFields.length} label={i('common.custom-fields')} />
                    <DisplayEntityInfo count={extraData?.pageTemplateHeaders.length} label={i('common.pages')} />
                </>
            ),
        },
        {
            title: i('case-template.list.table.columns.by'),
            className: 'case-template-list__column__by',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} avatarOnly />,
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'case-template-list__column__date',
            render: ({ _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'case-template-list__column__actions',
            render: (caseTemplate) => (
                <>
                    {hasPermissions.casetemplate && (
                        <ActionsList
                            actionList={{
                                edit: {
                                    label: i('general.edit'),
                                    icon: <ICONS.EditIcon />,
                                    CustomComponent: (triggerElem) => (
                                        <CaseTemplateManage
                                            triggerElem={triggerElem}
                                            defaultValues={caseTemplate}
                                            onEdit={(caseTemplate, caseTemplateId) => {
                                                if (caseTemplate.pageTemplateIds) {
                                                    eventList.onLinkPageTemplates(
                                                        caseTemplate.pageTemplateIds,
                                                        caseTemplateId,
                                                    );
                                                }
                                                return eventList.onEdit(caseTemplate, caseTemplateId);
                                            }}
                                            edit
                                        />
                                    ),
                                },
                                export: {
                                    label: i('case-template.actions.export'),
                                    icon: <ICONS.ExportOutlined />,
                                    query: () =>
                                        downloadFile(
                                            caseTemplate.name + '.json',
                                            JSON.stringify(caseTemplate),
                                            'application/json',
                                        ),
                                },
                                delete: {
                                    label: i('general.delete'),
                                    confirm: true,
                                    message: i('case-template.actions.delete.single'),
                                    icon: <ICONS.DeleteIcon />,
                                    query: () => eventList.onDelete(caseTemplate._id),
                                },
                            }}
                            isIcon
                            scope="single"
                        />
                    )}
                </>
            ),
        },
    ]);

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePageManager?.page?.currentView || '';

        setDefaultDisplay?.(currentView, {
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePageManager, showDates, setDefaultDisplay]);

    usePageTitle(i('page-title.organisation.templates.cases', { companyName: orgId }));

    return (
        <THLayout>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions.casetemplate && (
                            <>
                                <CaseTemplateManage
                                    triggerElem={
                                        <AddButton
                                            label={i('case-template.list.header.add-case-template')}
                                            disabled={!quotas?.case?.template?.valid}
                                        />
                                    }
                                    onCreate={(caseTemplate) => eventList.onCreate(caseTemplate)}
                                />

                                <TemplateImport
                                    mode="case-template"
                                    triggerElem={
                                        <THButton type="default" disabled={!quotas?.case?.template?.valid}>
                                            {i('case-template.list.header.import-case-template')}
                                        </THButton>
                                    }
                                    onCreate={(caseTemplate) => eventList.onCreate(caseTemplate)}
                                />
                            </>
                        )}

                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.CASE_TEMPLATE]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('case-template.none')}>
                            {hasPermissions.casetemplate && (
                                <CaseTemplateManage
                                    triggerElem={
                                        <THButton type="link">
                                            {i('case-template.list.header.add-case-template')}
                                        </THButton>
                                    }
                                    onCreate={(caseTemplate) => eventList.onCreate(caseTemplate)}
                                />
                            )}
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.caseTemplates');
const pathUser = RouteBuilder.compute('organisation.templates-case');

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.case-templates',
    },
    permissionNeeded: [PermissionsEnum.MANAGE_CASE_TEMPLATE],
    path,
    content: <CaseTemplatesList />,
    shared: true,
    space: ['admin'],
    breadcrumb: { label: 'breadcrumb.organisation.case-templates' },
};
export const RouteUser: RouteType = {
    ...Route,
    path: pathUser,
    space: ['user'],
};
